import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline, sfSub } from './sectionHelpers';

interface Testimonial {
  quote: string;
  author: string;
  role?: string;
  company?: string;
  avatar_url?: string;
  rating?: number | string;
}

// Card accent colours used for colorful-cards variant (cycles through)
const COLORFUL_PALETTES: Array<{ bg: string; star: string; border: string }> = [
  { bg: '#ecfdf5', star: '#10b981', border: '#6ee7b7' },
  { bg: '#eff6ff', star: '#3b82f6', border: '#93c5fd' },
  { bg: '#fdf4ff', star: '#a855f7', border: '#d8b4fe' },
  { bg: '#fff7ed', star: '#f97316', border: '#fdba74' },
  { bg: '#f0f9ff', star: '#0ea5e9', border: '#7dd3fc' },
  { bg: '#fef2f2', star: '#ef4444', border: '#fca5a5' },
];

const DEFAULT_TESTIMONIALS: Testimonial[] = [
  {
    quote: 'Working with this team has been a game-changer. The results exceeded all our expectations.',
    author: 'Alex Johnson',
    role: 'Marketing Director',
    company: 'Acme Corp',
    rating: 5,
  },
  {
    quote: 'Incredible service and attention to detail. I would highly recommend to anyone looking for quality.',
    author: 'Sarah Williams',
    role: 'Founder',
    company: 'StartupXYZ',
    rating: 5,
  },
  {
    quote: 'The best investment we made this year. Our ROI has been outstanding since we started.',
    author: 'Michael Chen',
    role: 'COO',
    company: 'TechCo',
    rating: 5,
  },
];

const TestimonialsGrid: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const variant = c.variant || section.variant || 'default';
  const isColorful = variant === 'colorful-cards' || variant === 'colorful';

  const testimonials: Testimonial[] = (Array.isArray(c.testimonials) && (c.testimonials as Testimonial[]).length > 0)
    ? (c.testimonials as Testimonial[])
    : DEFAULT_TESTIMONIALS;

  const cols = c.columns || Math.min(testimonials.length, 3);

  return (
    <section style={{ padding: '80px 24px', background: isColorful ? '#fff' : 'var(--brand-bg-alt, #f8fafc)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.5rem', letterSpacing: '-0.025em', marginBottom: 12, fontWeight: 700 }}
          >
            {sfHeadline(c, 'What Our Clients Say')}
          </InlineEditText>
          {sfSub(c) && (
            <InlineEditText
              tag="p"
              controlKey="subheadline"
              style={{ fontSize: '1.125rem', color: 'var(--brand-text-light, #64748b)', maxWidth: 640, margin: '0 auto' }}
            >
              {sfSub(c)}
            </InlineEditText>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 24 }}>
          {testimonials.map((t, i) => {
            const rating = Math.min(parseInt(String(t.rating ?? 5), 10) || 5, 5);
            const palette = isColorful ? COLORFUL_PALETTES[i % COLORFUL_PALETTES.length] : null;

            const cardStyle: React.CSSProperties = palette
              ? {
                  background: palette.bg,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 16,
                  padding: 28,
                  transition: 'all 0.3s',
                }
              : {
                  background: '#ffffff',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 12,
                  padding: 24,
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s',
                };

            const starColor = palette?.star || '#f59e0b';

            return (
              <div key={i} style={cardStyle}>
                {/* Avatar */}
                {t.avatar_url ? (
                  <img
                    src={t.avatar_url}
                    alt={t.author || 'Testimonial author'}
                    style={{
                      width: 48, height: 48, borderRadius: '50%', objectFit: 'cover',
                      border: `2px solid ${starColor}`, marginBottom: 12,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: starColor, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: '1.125rem', marginBottom: 12,
                  }}>
                    {t.author ? t.author.charAt(0).toUpperCase() : '?'}
                  </div>
                )}

                <div style={{ color: starColor, marginBottom: 12, fontSize: '1.1rem' }}>
                  {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
                </div>
                <blockquote style={{ fontStyle: 'italic', marginBottom: 16, lineHeight: 1.6, color: '#374151' }}>
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <strong style={{ color: '#111827' }}>{t.author}</strong>
                {(t.role || t.company) && (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {t.role}{t.company ? `, ${t.company}` : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsGrid;
